import { useRef, type ComponentProps, type FormEvent, type RefObject } from "react";
import type { ResolvedKeybindingsConfig } from "@cmmd-forge/contracts";
import type { HeadingSegments } from "./NewThreadLanding.logic";
import type { ProviderInteractionMode, RuntimeMode } from "@cmmd-forge/contracts";
import type { ComposerPromptEditorHandle } from "../ComposerPromptEditor";
import { ComposerFooterModeControls } from "../chat/ComposerFooterModeControls";
import { DraftHeroHeadline } from "../chat/DraftHeroHeadline";
import { ComposerPrimaryActions } from "../chat/ComposerPrimaryActions";
import { ComposerSurface } from "../chat/ComposerSurface";
import { ProviderModelPicker } from "../chat/ProviderModelPicker";
import { NO_ZOOM_FIELD_TEXT_CLASS } from "../../lib/touchTarget";
import { SidebarInset } from "../ui/sidebar";
import type {
  BranchChipProps,
  ExecutionChipProps,
  ProjectChipProps,
  WorktreeChipProps,
} from "./composerChips";
import { ComposerControlsStrip } from "../chat/ComposerControlsStrip";
import { useComposerFooterCompactness } from "../chat/useComposerFooterCompactness";
import { useComposerViewTransitionStyle } from "../chat/composerViewTransition";
import type { CloudRuntimeTerminalFailureResidual } from "../cloudRuntimeTerminalFailureResidual";
import { NewThreadSubmissionFeedback } from "./NewThreadSubmissionFeedback";
import { NewThreadMobileSetup } from "./NewThreadMobileSetup";
import type { ComposerFileAttachment, ComposerImageAttachment } from "../../composerDraftStore";
import type { TerminalContextDraft } from "../../lib/terminalContext";
import { ComposerImageAttachmentButton } from "../chat/ComposerImageAttachmentButton";
import type { ComposerImageAttachmentController } from "../chat/useComposerImageAttachments";
import type { ExpandedImagePreview } from "../chat/ExpandedImagePreview";
import type { NewThreadLandingSurface } from "./newThreadLandingSurface";
import { NewThreadInventoryNotice } from "./NewThreadInventoryNotice";
import { ComposerStashToolbarControl } from "../chat/ComposerStashMenu";
import { CompactComposerControlsMenu } from "../chat/CompactComposerControlsMenu";
import type {
  ComposerStashController,
  ComposerStashNoticeType,
} from "../chat/useComposerStashController";

type ProviderModelPickerProps = ComponentProps<typeof ProviderModelPicker>;
const noop = () => {};

/**
 * Everything the composer knows about where a turn will run.
 *
 * `project` never reaches the strip: the picker lives in the hero headline now,
 * and t3code's own strip carries Environment, worktree mode and branch only.
 * Passing it to the strip as well is how the draft ended up with two triggers
 * for one setting.
 */
interface NewThreadContextProps {
  readonly execution: ExecutionChipProps;
  readonly project: ProjectChipProps;
  readonly worktree: WorktreeChipProps;
  readonly branch: BranchChipProps;
}

interface NewThreadComposerControlsProps {
  readonly interactionMode: ProviderInteractionMode;
  readonly runtimeMode: RuntimeMode;
  readonly providerModel: ProviderModelPickerProps;
  readonly onToggleInteractionMode: () => void;
  readonly onRuntimeModeChange: (mode: RuntimeMode) => void;
  readonly stash?: ComposerStashController | undefined;
  readonly stashShortcutLabel?: string | null | undefined;
  readonly stashDisabled?: boolean | undefined;
  readonly onStashNotice?: ((message: string, type: ComposerStashNoticeType) => void) | undefined;
}

interface NewThreadComposerControlsRendererProps extends NewThreadComposerControlsProps {
  /** Uses the shared compact model-picker geometry below the footer threshold. */
  readonly footerCompact: boolean;
}

interface NewThreadComposerFeedbackProps {
  readonly cloudCatalogActive: boolean;
  readonly error: string | null;
  readonly errorCode: string | null;
  readonly failureResidual?: CloudRuntimeTerminalFailureResidual | null;
  readonly localNotice?: string | null;
  /** The connect flow's current phase, while a cold cloud runtime provisions. */
  readonly sendingPhase?: string | null;
  readonly onRetry: () => void;
}

export interface NewThreadComposerProps {
  readonly inventoryState?: NewThreadLandingSurface;
  readonly headingSegments: HeadingSegments;
  readonly resolvedTheme: "light" | "dark";
  readonly editorRef: RefObject<ComposerPromptEditorHandle | null>;
  readonly prompt: string;
  readonly cursor: number;
  readonly placeholder: string;
  readonly submitting: boolean;
  /**
   * The connect flow's progress summary while `submitting` is a cold cloud
   * runtime provisioning, or null/undefined for a bare "Sending" (FOR-3579).
   */
  readonly sendingLabel?: string | null;
  readonly hasSendableContent: boolean;
  readonly images: ReadonlyArray<ComposerImageAttachment>;
  readonly files?: ReadonlyArray<ComposerFileAttachment> | undefined;
  readonly terminalContexts?: ReadonlyArray<TerminalContextDraft> | undefined;
  readonly keybindings?: ResolvedKeybindingsConfig | undefined;
  readonly imageAttachments: ComposerImageAttachmentController;
  readonly context: NewThreadContextProps;
  readonly controls: NewThreadComposerControlsProps;
  readonly feedback: NewThreadComposerFeedbackProps;
  readonly onPromptChange: (value: string, cursor: number) => void;
  readonly onCommandKeyDown: (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
  ) => boolean;
  readonly onSubmit: () => void;
  readonly onRemoveImage: (imageId: string) => void;
  readonly onRemoveFile?: ((fileId: string) => void) | undefined;
  readonly onExpandImage: (preview: ExpandedImagePreview) => void;
  readonly onRemoveTerminalContext?: ((contextId: string) => void) | undefined;
  readonly stash?: ComposerStashController | undefined;
  readonly stashShortcutLabel?: string | null | undefined;
  readonly onStashNotice?: ((message: string, type: ComposerStashNoticeType) => void) | undefined;
}

function NewThreadComposerControls(props: NewThreadComposerControlsRendererProps) {
  return (
    <>
      <ProviderModelPicker
        {...props.providerModel}
        compact={props.footerCompact}
        triggerClassName="min-h-touch sm:min-h-touch md:min-h-0"
      />
      <div className="hidden md:contents">
        <ComposerFooterModeControls
          interactionMode={props.interactionMode}
          runtimeMode={props.runtimeMode}
          onToggleInteractionMode={props.onToggleInteractionMode}
          onRuntimeModeChange={props.onRuntimeModeChange}
        />
      </div>
      {props.stash ? (
        <>
          <div className="hidden md:block">
            <ComposerStashToolbarControl
              adapter={props.stash}
              disabled={props.stashDisabled ?? false}
              onResult={props.onStashNotice ?? noop}
              shortcutLabel={props.stashShortcutLabel}
            />
          </div>
          <div className="md:hidden">
            <CompactComposerControlsMenu
              activePlan={false}
              interactionMode={props.interactionMode}
              planSidebarLabel="Plan"
              planSidebarOpen={false}
              runtimeMode={props.runtimeMode}
              stash={{
                adapter: props.stash,
                disabled: props.stashDisabled ?? false,
                onResult: props.onStashNotice ?? noop,
                shortcutLabel: props.stashShortcutLabel ?? null,
              }}
              onToggleInteractionMode={props.onToggleInteractionMode}
              onTogglePlanSidebar={noop}
              onRuntimeModeChange={props.onRuntimeModeChange}
            />
          </div>
        </>
      ) : null}
    </>
  );
}

function NewThreadSubmitAction(props: {
  readonly promptHasText: boolean;
  readonly submitting: boolean;
  readonly sendingLabel?: string | null;
  readonly hasSendableContent: boolean;
}) {
  return (
    <ComposerPrimaryActions
      compact={false}
      sendVisualDensity="compact"
      pendingAction={null}
      isRunning={false}
      showPlanFollowUpPrompt={false}
      promptHasText={props.promptHasText}
      isSendBusy={props.submitting}
      sendingLabel={props.sendingLabel ?? null}
      isConnecting={false}
      isPreparingWorktree={false}
      hasSendableContent={props.hasSendableContent}
      isComposerDisabled={false}
      onPreviousPendingQuestion={noop}
      onInterrupt={noop}
      onImplementPlanInNewThread={noop}
    />
  );
}

export function NewThreadComposer(props: NewThreadComposerProps) {
  const composerViewTransitionStyle = useComposerViewTransitionStyle();
  const composerFormRef = useRef<HTMLFormElement | null>(null);
  // The same measurement the thread Composer uses, so the landing folds at the
  // same widths. Its footer never carries the wide follow-up actions and never
  // swaps which actions it renders, so the layout key is a constant and there
  // is no thread to re-measure on.
  const { footerCompact, primaryActionsCompact } = useComposerFooterCompactness({
    composerFormRef,
    hasWideActions: false,
    actionLayoutKey: "landing",
    activeThreadId: null,
  });
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    props.onSubmit();
  };

  return (
    <SidebarInset className="relative h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex h-full w-full flex-col items-center justify-start px-3 pt-16 pb-[max(1.5rem,env(safe-area-inset-bottom))] [@media(max-height:500px)]:pt-4 sm:px-6 sm:pt-20 md:justify-center md:py-10">
        <div className="flex w-full max-w-3xl min-w-0 flex-col items-stretch gap-5 [@media(max-height:500px)]:gap-3 md:gap-8">
          <DraftHeroHeadline
            headingSegments={props.headingSegments}
            project={props.context.project}
          />
          <NewThreadInventoryNotice state={props.inventoryState ?? "composer"} />

          <div className="min-w-0" style={composerViewTransitionStyle}>
            {/* No `frameClassName`/`surfaceClassName`/radius overrides: the
                landing composer and the in-thread composer are the same
                component and must read as the same object across the
                new-thread → thread transition. Every override here used to
                change a radius, which is exactly what made them look like two
                different composers. */}
            <ComposerSurface
              formRef={composerFormRef}
              footerCompact={footerCompact}
              primaryActionsCompact={primaryActionsCompact}
              keybindings={props.keybindings}
              onStashShortcut={props.submitting ? undefined : props.stash?.save}
              onSubmit={handleSubmit}
              isDragOver={props.imageAttachments.isDragOver}
              onDragEnter={props.imageAttachments.onDragEnter}
              onDragOver={props.imageAttachments.onDragOver}
              onDragLeave={props.imageAttachments.onDragLeave}
              onDrop={props.imageAttachments.onDrop}
              contextStrip={
                <>
                  <NewThreadMobileSetup
                    {...props.context}
                    interactionMode={props.controls.interactionMode}
                    runtimeMode={props.controls.runtimeMode}
                    onToggleInteractionMode={props.controls.onToggleInteractionMode}
                    onRuntimeModeChange={props.controls.onRuntimeModeChange}
                  />
                  <div className="hidden w-full min-w-0 md:contents">
                    <ComposerControlsStrip
                      mode="landing"
                      execution={props.context.execution}
                      worktree={props.context.worktree}
                      branch={props.context.branch}
                    />
                  </div>
                </>
              }
              menu={{ open: false, resolvedTheme: props.resolvedTheme }}
              images={props.images}
              files={props.files}
              onExpandImage={props.onExpandImage}
              onRemoveImage={props.onRemoveImage}
              onRemoveFile={props.onRemoveFile}
              promptEditor={{
                ref: props.editorRef,
                value: props.prompt,
                cursor: props.cursor,
                terminalContexts: props.terminalContexts ?? [],
                skills: [],
                onRemoveTerminalContext: props.onRemoveTerminalContext ?? noop,
                onChange: props.onPromptChange,
                onCommandKeyDown: props.onCommandKeyDown,
                onPaste: props.imageAttachments.onPaste,
                placeholder: props.placeholder,
                disabled: props.submitting,
                className: `${NO_ZOOM_FIELD_TEXT_CLASS} min-h-20 md:min-h-14`,
              }}
              footerLeft={
                <NewThreadComposerControls
                  footerCompact={footerCompact}
                  interactionMode={props.controls.interactionMode}
                  runtimeMode={props.controls.runtimeMode}
                  providerModel={props.controls.providerModel}
                  onToggleInteractionMode={props.controls.onToggleInteractionMode}
                  onRuntimeModeChange={props.controls.onRuntimeModeChange}
                  stash={props.stash}
                  stashShortcutLabel={props.stashShortcutLabel}
                  stashDisabled={props.submitting}
                  onStashNotice={props.onStashNotice}
                />
              }
              // Layout only — the dock carries no fill of its own any more, so
              // there is nothing left here to override away.
              footerLeftClassName="turn-chip-strip max-w-none flex-1 grow gap-2 md:max-w-full md:flex-none md:grow-0 md:gap-1"
              footerRight={
                <>
                  <ComposerImageAttachmentButton
                    disabled={props.submitting}
                    onFilesSelected={(files) => {
                      props.imageAttachments.addFiles(files);
                      props.editorRef.current?.focusAt(props.prompt.length);
                    }}
                  />
                  <NewThreadSubmitAction
                    promptHasText={props.prompt.trim().length > 0}
                    submitting={props.submitting}
                    sendingLabel={props.sendingLabel ?? null}
                    hasSendableContent={props.hasSendableContent}
                  />
                </>
              }
            />

            <NewThreadSubmissionFeedback {...props.feedback} />
          </div>
        </div>
      </div>
    </SidebarInset>
  );
}
